import type { ComposerAttachment } from "../../composer/types";
import type { DocumentAttachment } from "./MessageDocuments";
import type {
  Message,
  AIMessage as SDKAIMessage,
  HumanMessage as SDKHumanMessage,
  ToolMessage as SDKToolMessage,
} from "@langchain/langgraph-sdk";

// ─────────────────────────────────────────────────────────────────────────
// Re-export LangGraph SDK message types as the canonical wire format
// ─────────────────────────────────────────────────────────────────────────

export type { Message, SDKAIMessage as AIMessage, SDKHumanMessage as HumanMessage, SDKToolMessage as ToolMessage };

// MessageContent matches the SDK's definition: string | ContentPart[]
type MessageContentText = { type: "text"; text: string };
type MessageContentImageUrl = {
  type: "image_url";
  image_url: string | { url: string; detail?: "auto" | "low" | "high" };
};
type MessageContentDocument = {
  type: "document";
  source: { type: "url"; url: string };
};
type MessageContentComplex = MessageContentText | MessageContentImageUrl | MessageContentDocument;
export type MessageContent = string | MessageContentComplex[];

// Re-export these for consumers who need fine-grained content part types
export type { MessageContentText, MessageContentImageUrl, MessageContentDocument, MessageContentComplex };

// ─────────────────────────────────────────────────────────────────────────
// Chat attachment types (for composer / upload UI)
// ─────────────────────────────────────────────────────────────────────────

export interface ChatAttachment {
  id: string;
  file?: File;
  status: "uploading" | "completed" | "error";
  type: "image" | "document";
  previewUrl?: string;
  url?: string;
  errorMessage?: string;
}

export function toDisplayAttachment(attachment: ComposerAttachment): ChatAttachment {
  return {
    id: attachment.id,
    file: attachment.file,
    status: attachment.status,
    type: attachment.file?.type.startsWith("image/") ? "image" : "document",
    previewUrl: attachment.previewUrl,
    url: attachment.url,
    errorMessage: attachment.errorMessage,
  };
}

export function fileToAttachment(file: File, index: number): ChatAttachment {
  return {
    id: `file-${index}-${file.name}`,
    file,
    status: "completed",
    type: file.type.startsWith("image/") ? "image" : "document",
  };
}

export function filesToAttachments(files: File[]): ChatAttachment[] {
  return files.map((file, index) => fileToAttachment(file, index));
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers for working with MessageContent
// ─────────────────────────────────────────────────────────────────────────

/** Extract plain text from MessageContent (string or content parts). */
export function extractText(content: MessageContent | undefined | null): string {
  if (!content) return "";
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .filter((p): p is MessageContentText => p.type === "text")
    .map((p) => p.text)
    .join("");
}

/** Extract image URLs from MessageContent. */
export function extractImageUrls(content: MessageContent | undefined | null): string[] {
  if (!content || typeof content === "string") return [];
  if (!Array.isArray(content)) return [];
  return content
    .filter((p): p is MessageContentImageUrl => p.type === "image_url")
    .map((p) => (typeof p.image_url === "string" ? p.image_url : p.image_url.url));
}

/**
 * Extract document attachments from MessageContent.
 * Filenames are looked up from additional_kwargs.document_filenames
 * (persisted in LangGraph state, not sent to the LLM API).
 */
export function extractDocuments(
  content: MessageContent | undefined | null,
  additionalKwargs?: Record<string, unknown>,
): DocumentAttachment[] {
  if (!content || typeof content === "string") return [];
  if (!Array.isArray(content)) return [];
  const filenames = (additionalKwargs?.document_filenames ?? {}) as Record<string, string>;
  const mimeTypes = (additionalKwargs?.document_mime_types ?? {}) as Record<string, string>;
  return content
    .filter((p): p is MessageContentDocument => p.type === "document")
    .map((p) => ({
      url: p.source.url,
      filename: filenames[p.source.url],
      mimeType: mimeTypes[p.source.url],
    }));
}
